import React, {useEffect, useRef, useState} from 'react';
import Benefits from "./benefits/Benefits.jsx";
import Price from "./Price.jsx";
import Selection from "./selection/Selection.jsx";
import {Link, Typography} from "@bonprix/design-system";
import FitFinder from "./FitFinder.jsx";
import ProductAvailability from "./ProductAvailability.jsx";
import Buttons from "./Buttons.jsx";
import ProductStats from "./product-stats/ProductStats.jsx";
import BenefitSection from "./BenefitSection.jsx";
import ProductRating from "./ProductRating.jsx";
import Landmark from "@core/scripts/components/landmark-adapter";
import PriceReduction from "@product/components/PriceReduction.jsx";
import SizeAdvice from "@product/components/size-advice/SizeAdvice.jsx";
import Breakpoint from "@core/scripts/helper/breakpoint.js";
import MessageBroker from "@bonprix/pattern-library/components/message-broker.js";
import Messages from "@core/scripts/components/messages.js";

export default function Details({
                                    product,
                                    texts,
                                    staticLinks,
                                    sizeGuideTableUrl,
                                    pointOfSaleIdOverride,
                                    filterTrackingValue,
                                    rankingTrackingValue,
                                    pageViewId,
                                    reviewContent,
                                    wishlistEnabled,
                                    fullWidth,
                                    hideFitFinder,
                                    sizeAdvice,
                                    priceReductionCampaign
                                }) {

    const [sizeAdviceData, setSizeAdviceData] = useState({loaded: false, data: null});
    const [isMobileView, setIsMobileView] = useState(Breakpoint.keyIsIn(['start', 'small', 'medium']));

    const sizeTableRef = useRef(null);

    const updateMobileView = () => setIsMobileView(Breakpoint.keyIsIn(['start', 'small', 'medium']));

    useEffect(() => {
        const subscription = MessageBroker.subscribe(Messages.BREAKPOINT.CHANGED, updateMobileView);
        return () => MessageBroker.unsubscribe(subscription);
    }, []);

    const sendSizeTableLinkLandmark = () => Landmark.sendClickLandmark('adp_sizechart');
    const onSizeAdviceLoaded = (data) => setSizeAdviceData({loaded: true, data});

    const hideOrderHint = sizeAdvice && (!sizeAdviceData.loaded || sizeAdviceData.data);

    return (
        <>
            {product.currentItem.benefits?.length > 0 &&
                <div className={fullWidth ? "product__benefits__full_width" : "product__benefits"}>
                    <Benefits product={product}/>
                </div>
            }

            {isMobileView ?
                <>
                    <div className="product__prices-container-wrapper">
                        <Price staticLinks={staticLinks} texts={texts} product={product} fullWidth={fullWidth}/>
                    </div>

                    <PriceReduction priceReductionCampaign={priceReductionCampaign}/>

                    <div className={fullWidth ? "product__name__full_width" : "product__name"}>
                        <Typography variant="bonprix-h4" component="h1">{product.name}</Typography>
                    </div>

                    {reviewContent?.count > 0 && <div className={fullWidth ? "product__rating__full_width" : "product__rating"}>
                        <ProductRating
                            rating={reviewContent?.averageRating}
                            ratingQuantity={reviewContent?.count}                     
                            fullWidth={fullWidth}
                            ariaLabel={texts.product_rating_aria_label}
                        />
                    </div>}
                </>
                :
                <>
                    <div className={fullWidth ? "product__name__full_width" : "product__name"}>
                        <Typography variant="bonprix-h4" component="h1">{product.name}</Typography>
                    </div>

                    {reviewContent?.count > 0 &&
                        <div className={fullWidth ? "product__rating__full_width" : "product__rating"}>
                            <ProductRating
                                rating={reviewContent?.averageRating}
                                ratingQuantity={reviewContent?.count}
                                fullWidth={fullWidth}
                                ariaLabel={texts.product_rating_aria_label}
                            />
                        </div>}

                    <div className="product__prices-container-wrapper">
                        <Price staticLinks={staticLinks} texts={texts} product={product} fullWidth={fullWidth}/>
                    </div>

                    <PriceReduction priceReductionCampaign={priceReductionCampaign}/>
                </>
            }

            <div className={fullWidth ? 'mt-6 w-full' : 'product__selection'}>
                <Selection texts={texts} product={product} fullWidth={fullWidth} hideOrderHint={false}/>
                {/*ToDo: uncomment when size advice API is ready*/}
                {/*<Selection texts={texts} product={product} fullWidth={fullWidth} hideOrderHint={hideOrderHint}/>*/}
            </div>

            {sizeAdvice && <SizeAdvice texts={texts} product={product} fullWidth={fullWidth} onLoaded={onSizeAdviceLoaded}/>}

            <div className={fullWidth ? "product__size-table__full_width" : "product__size-table"} ref={sizeTableRef}>
                {product.showFitAnalytics && (
                // ToDo: uncomment when size advice API is ready
                // {!sizeAdvice && product.showFitAnalytics && (
                    <FitFinder product={product} texts={texts} sizeTableRef={sizeTableRef} fullWidth={fullWidth} hideFitFinder={hideFitFinder}/>
                )}

                {product.showSizeTableLink && (
                    <Link
                        arrowPosition='right'
                        href={sizeGuideTableUrl}
                        showArrow
                        hideUnderline
                        onClick={sendSizeTableLinkLandmark}
                        target="_blank"
                    >
                        {texts.bff_ada_sizeTable}
                    </Link>
                )}

                {product.showBraAdvisorLink && (
                    <Link
                        arrowPosition='right'
                        href={staticLinks.braAdvisorUrl}
                        target='_blank'
                        showArrow
                        hideUnderline
                    >
                        {texts.bra_advisor_link_text}
                    </Link>
                )}
            </div>

            <div className={fullWidth ? "product__availability__full_width" : "product__availability"}>
            <ProductAvailability texts={texts} product={product}/>
            </div>

            <div className={fullWidth ? "product__buttons__full_width" : "product__buttons"}>
                <Buttons texts={texts} product={product} pointOfSaleIdOverride={pointOfSaleIdOverride}
                         filterTrackingValue={filterTrackingValue} rankingTrackingValue={rankingTrackingValue} pageViewId={pageViewId}
                         wishlistEnabled={wishlistEnabled} fullWidth={fullWidth}/>
            </div>

            <div className={fullWidth ? "product__stats__full_width" : "product__stats"}>
                <ProductStats texts={texts} styleId={product.styleId}/>
            </div>

            {product.benefitSection.items.length > 0 && (
                <BenefitSection product={product} fullWidth={fullWidth}/>
            )}
        </>
    );
}
